import React from "react";
import { Button, Input, Typography, Grid } from "antd";
import { useNavigate } from "react-router-dom";
import { useUserOnboarding } from "../../../../Context/UserOnboardingContext";
import jobDescriptionService from "../../../../Service/job.description.service";
import AuthService from "../../../../Service/auth.service";
import Paragraph from "antd/es/typography/Paragraph";
import { classifyError } from "../../../../Utils/errorClassifier";
import WizardErrorPanel from "./WizardErrorPanel";

const { Title } = Typography;
const { TextArea } = Input;
const { useBreakpoint } = Grid;

const INTRO_STEPS = [
  { num: "1", label: "Enter Job Description" },
  { num: "2", label: "AI Structures It" },
  { num: "3", label: "Review & Edit" },
  { num: "4", label: "Publish Job Post" },
];

const FreshStartWizard: React.FC = () => {
  const navigate = useNavigate();
  const screens = useBreakpoint();
  const {
    jobDescription,
    setJobDescription,
    currentStep,
    setCurrentStep,
    setErrorMessage,
    errorMessage,
    errorCategory,
    setErrorCategory,
    failedStep,
    setFailedStep,
  } = useUserOnboarding();


  const handleProceed = async () => {
    if (!jobDescription.trim()) return;
    console.log("[FreshStartWizard] handleProceed — generateJobDescription START");
    setCurrentStep("job-post-processing");
    setErrorCategory(null);
    setFailedStep(null);
    try {
      const generated = await jobDescriptionService.generateJobDescription(jobDescription.trim());
      if (!generated) throw new Error("No response from AI service.");
      sessionStorage.setItem("onboarding_generated_job_post", JSON.stringify(generated));
      console.log("[FreshStartWizard] handleProceed ✓ stored, navigating to add-job-post");
      setCurrentStep("completed");
    } catch (err: any) {
      console.error("[FreshStartWizard] handleProceed ✗", err);
      const classified = classifyError(err);
      setErrorCategory(classified.category);
      setFailedStep('job-post-generation');
      setErrorMessage(err?.message || "Failed to generate job post. Please try again.");
      setCurrentStep("error");
    }
  };

  const handleSkip = () => {
    const user = AuthService.getInstance().getCurrentUser();
    if (user?._id) localStorage.setItem(`onboarding_completed_${user._id}`, "true");
    sessionStorage.removeItem("onboarding_generated_job_post");
    navigate("/");
  };

  const handleNavigateAfterCompletion = () => {
    sessionStorage.setItem("onboarding_redirect", "true");
    navigate("/add-job-post?mode=new", { replace: true });
  };

  const isProcessing = currentStep === "job-post-processing" || currentStep === "completed";
  const isError = currentStep === "error";

  return (
    <div className="uo-processing-console">
      <div className="uo-processing-console__header">
        <Title level={3}>Create Your First Job Post</Title>
        <div className="uo-intro-steps">
          {INTRO_STEPS.map((s, i) => (
            <React.Fragment key={s.num}>
              <div className="uo-intro-step">
                <span className="uo-intro-step__num">{s.num}</span>
                <span className="uo-intro-step__label">{s.label}</span>
              </div>
              {i < INTRO_STEPS.length - 1 && (
                <span className="uo-intro-step__arrow">→</span>
              )}
            </React.Fragment>
          ))}
        </div>
      </div>

      <div
        className="uo-processing-console__body"
        style={{
          flexDirection: screens.md !== false ? "row" : "column",
        }}
      >
        <div className="uo-processing-console__terminal">
          <div style={{ color: "#4b5563", marginBottom: 12, fontSize: 12 }}>
            ■ <span className="term-system">Job description entry...</span>
          </div>

          <div style={{ marginTop: 16 }}>
            <TextArea
              value={jobDescription}
              onChange={(e) => setJobDescription(e.target.value)}
              disabled={isProcessing || isError}
              rows={15}
              placeholder="Paste your job description here..."
              className={isProcessing ? "uo-textarea-scanning" : ""}
              style={{
                borderRadius: 4,
                fontSize: 14,
                resize: "vertical",
              }}
            />
          </div>
        </div>

        <div className="uo-processing-console__right-panel">
          <div className="processing-center">
            <div className="connecting-line left-line" />
            <div className="processing-orb">
              <img src="/icons/ai-stars.svg" alt="AI" />
            </div>
            <div className="connecting-line right-line" />
          </div>

          {isError && errorCategory && failedStep ? (
            <WizardErrorPanel
              classified={{ category: errorCategory, isRetryable: false, requiresUserAction: false }}
              failedStep={failedStep}
              onRetry={handleProceed}
              onSkip={() => navigate("/")}
              onGoToDashboard={() => navigate("/")}
              isRecruiter={false}
            />
          ) : (
            <>
              <div className="uo-processing-console__status">
                {isProcessing ? (
                  <>
                    <Typography.Title level={5}>Processing Your Job Post</Typography.Title>
                    <Typography.Paragraph type="secondary">
                      {currentStep === "job-post-processing"
                        ? "Our AI is generating a structured job post..."
                        : "Job post created successfully!"}
                    </Typography.Paragraph>
                  </>
                ) : (
                  <>
                    <Typography.Title level={5}>Create Your Job Post</Typography.Title>
                    <Typography.Paragraph type="secondary">
                      Enter your job description on the left. Our AI will generate a
                      structured job post you can review and edit before publishing.
                    </Typography.Paragraph>
                  </>
                )}
              </div>

              <div className="uo-processing-console__actions">
                {isProcessing ? (
                  <>
                    {currentStep === "completed" && (
                      <Button
                        block
                        type="primary"
                        onClick={handleNavigateAfterCompletion}
                        size="large"
                      >
                        Continue →
                      </Button>
                    )}
                    {currentStep === "job-post-processing" && (
                      <div style={{ textAlign: "center", width: "100%" }}>
                        <div className="ant-spin-nested-loading" style={{ textAlign: "center" }}>
                          <div className="ant-spin ant-spin-spinning" style={{ fontSize: 24 }}>
                            <span className="ant-spin-dot ant-spin-dot-spin">
                              <i></i><i></i><i></i><i></i>
                            </span>
                          </div>
                        </div>
                      </div>
                    )}
                  </>
                ) : (
                  <>
                    <Button
                      block
                      type="primary"
                      disabled={!jobDescription.trim()}
                      onClick={handleProceed}
                      size="large"
                    >
                      Proceed →
                    </Button>
                    <Button type="text" block onClick={handleSkip} size="large">
                      Skip for now
                    </Button>
                  </>
                )}
              </div>
            </>
          )}
        </div>
      </div>

      <Typography.Paragraph type="secondary" className="uo-processing-console__footer">
        Once you proceed, our AI will generate a structured job post ready for
        you to review and publish.
      </Typography.Paragraph>
    </div>
  );
};

export default FreshStartWizard;
